import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Trash2, Plus, Save, RotateCcw } from "lucide-react";

/**
 * Phase 2 · Admin Workflow Console.
 * Lets the org admin configure material types, workflow toggles, approval
 * slabs, COGS composition and master price list.
 */
export default function WorkflowConsoleTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/workflow-config").then((r) => setCfg(r.data)).catch(() => toast.error("Failed to load workflow config"));
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/workflow-config", cfg);
      toast.success("Workflow config saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  // ---- Material types
  const updateMt = (i, patch) => setCfg({ ...cfg, material_types: cfg.material_types.map((m, j) => j === i ? { ...m, ...patch } : m) });
  const addMt = () => {
    const key = `custom_${Date.now()}`;
    setCfg({ ...cfg, material_types: [...cfg.material_types, { key, label: "New type", is_service: false, requires_stores_alloc: true, requires_qc: false, requires_batching: false, active: true }] });
  };
  const rmMt = (i) => setCfg({ ...cfg, material_types: cfg.material_types.filter((_, j) => j !== i) });

  // ---- Flow toggles
  const toggleFlow = (which, i) => {
    const key = which === "goods" ? "goods_flow" : "service_flow";
    setCfg({ ...cfg, [key]: cfg[key].map((s, j) => j === i ? { ...s, enabled: !s.enabled } : s) });
  };

  // ---- Approval slabs
  const updateSlab = (doc, i, patch) => setCfg({
    ...cfg,
    approval_slabs: {
      ...cfg.approval_slabs,
      [doc]: cfg.approval_slabs[doc].map((s, j) => j === i ? { ...s, ...patch } : s),
    }
  });
  const addSlab = (doc) => setCfg({
    ...cfg,
    approval_slabs: {
      ...cfg.approval_slabs,
      [doc]: [...cfg.approval_slabs[doc], { max: 100000, role: "hod", users: [] }],
    }
  });
  const rmSlab = (doc, i) => setCfg({
    ...cfg,
    approval_slabs: {
      ...cfg.approval_slabs,
      [doc]: cfg.approval_slabs[doc].filter((_, j) => j !== i),
    }
  });

  // ---- COGS composition
  const toggleCogs = (i) => setCfg({ ...cfg, cogs_composition: cfg.cogs_composition.map((c, j) => j === i ? { ...c, enabled: !c.enabled } : c) });
  const addCogs = () => setCfg({ ...cfg, cogs_composition: [...cfg.cogs_composition, { key: `custom_${Date.now()}`, label: "New cost line", enabled: true }] });
  const rmCogs = (i) => setCfg({ ...cfg, cogs_composition: cfg.cogs_composition.filter((_, j) => j !== i) });

  return (
    <div className="space-y-6" data-testid="workflow-console-tab">

      {/* --- SAVE BAR --- */}
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// Workflow console</div>
          <div className="text-lg font-heading">Configure PR/PO/GRN behaviour per material type</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="rounded-sm gap-1"><RotateCcw className="w-3.5 h-3.5" /> Reset</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm gap-1 bg-accent hover:bg-accent/90 text-white" data-testid="wc-save">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save config"}
          </Button>
        </div>
      </div>

      {/* --- MATERIAL TYPES --- */}
      <Card className="rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// Material types</div>
            <div className="text-sm font-medium">Per-type workflow toggles</div>
          </div>
          <Button size="sm" variant="outline" onClick={addMt} className="rounded-sm gap-1" data-testid="wc-add-mt"><Plus className="w-3 h-3" /> Add type</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40">
                <th className="p-2 text-left">Key</th>
                <th className="p-2 text-left">Label</th>
                <th className="p-2">Service</th>
                <th className="p-2">Stores alloc</th>
                <th className="p-2">QC required</th>
                <th className="p-2">Batching</th>
                <th className="p-2">Active</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {cfg.material_types.map((m, i) => (
                <tr key={m.key} className="border-t" data-testid={`wc-mt-${m.key}`}>
                  <td className="p-2 font-mono text-xs">{m.key}</td>
                  <td className="p-2"><Input value={m.label} onChange={(e) => updateMt(i, { label: e.target.value })} className="rounded-sm h-8 text-sm" /></td>
                  <td className="p-2 text-center"><Switch checked={m.is_service} onCheckedChange={(v) => updateMt(i, { is_service: v, requires_stores_alloc: v ? false : m.requires_stores_alloc })} /></td>
                  <td className="p-2 text-center"><Switch checked={m.requires_stores_alloc} disabled={m.is_service} onCheckedChange={(v) => updateMt(i, { requires_stores_alloc: v })} /></td>
                  <td className="p-2 text-center"><Switch checked={m.requires_qc} onCheckedChange={(v) => updateMt(i, { requires_qc: v })} /></td>
                  <td className="p-2 text-center"><Switch checked={m.requires_batching} onCheckedChange={(v) => updateMt(i, { requires_batching: v })} /></td>
                  <td className="p-2 text-center"><Switch checked={m.active} onCheckedChange={(v) => updateMt(i, { active: v })} /></td>
                  <td className="p-2 text-right"><Button size="sm" variant="ghost" onClick={() => rmMt(i)} className="h-7"><Trash2 className="w-3.5 h-3.5" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* --- SALES FLOW TOGGLES --- */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="rounded-sm p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">// Goods sales flow</div>
          <div className="text-sm font-medium mb-3">Which stages to keep for Goods</div>
          <div className="space-y-2">
            {cfg.goods_flow.map((s, i) => (
              <label key={s.key} className="flex items-center justify-between gap-3 text-sm" data-testid={`wc-goods-${s.key}`}>
                <span>{s.label}</span>
                <Switch checked={s.enabled} onCheckedChange={() => toggleFlow("goods", i)} />
              </label>
            ))}
          </div>
        </Card>
        <Card className="rounded-sm p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">// Service sales flow</div>
          <div className="text-sm font-medium mb-3">Which stages to keep for Services</div>
          <div className="space-y-2">
            {cfg.service_flow.map((s, i) => (
              <label key={s.key} className="flex items-center justify-between gap-3 text-sm" data-testid={`wc-service-${s.key}`}>
                <span>{s.label}</span>
                <Switch checked={s.enabled} onCheckedChange={() => toggleFlow("service", i)} />
              </label>
            ))}
          </div>
        </Card>
      </div>

      {/* --- APPROVAL SLABS --- */}
      <Card className="rounded-sm p-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">// Approval slabs · role-by-default + named-user override</div>
        <div className="grid md:grid-cols-2 gap-6">
          {["pr", "po", "bill", "payment"].map((doc) => (
            <div key={doc} data-testid={`wc-slab-${doc}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium capitalize">{doc.toUpperCase()} slabs</div>
                <Button size="sm" variant="ghost" onClick={() => addSlab(doc)} className="h-7 gap-1"><Plus className="w-3 h-3" /> Slab</Button>
              </div>
              <table className="w-full text-sm">
                <thead className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40">
                  <tr><th className="p-1 text-right">Up to ₹</th><th className="p-1 text-left">Role</th><th className="p-1 text-left">Named users (comma-sep)</th><th></th></tr>
                </thead>
                <tbody>
                  {cfg.approval_slabs[doc].map((s, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-1"><Input type="number" value={s.max} onChange={(e) => updateSlab(doc, i, { max: Number(e.target.value) })} className="rounded-sm h-7 text-xs tabular-nums text-right" /></td>
                      <td className="p-1"><Input value={s.role} onChange={(e) => updateSlab(doc, i, { role: e.target.value })} className="rounded-sm h-7 text-xs" placeholder="hod" /></td>
                      <td className="p-1"><Input value={(s.users || []).join(", ")} onChange={(e) => updateSlab(doc, i, { users: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="rounded-sm h-7 text-xs font-mono" placeholder="user1@x.com" /></td>
                      <td className="p-1 text-right"><Button size="sm" variant="ghost" onClick={() => rmSlab(doc, i)} className="h-7"><Trash2 className="w-3.5 h-3.5" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-[10px] text-muted-foreground mt-1">Use <span className="font-mono">-1</span> for "no upper limit".</div>
            </div>
          ))}
        </div>
      </Card>

      {/* --- COGS + MISC --- */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// COGS composition</div>
              <div className="text-sm font-medium">What builds up Cost of Goods Sold</div>
            </div>
            <Button size="sm" variant="outline" onClick={addCogs} className="rounded-sm gap-1"><Plus className="w-3 h-3" /> Line</Button>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {cfg.cogs_composition.map((c, i) => (
                <tr key={c.key} className="border-t" data-testid={`wc-cogs-${c.key}`}>
                  <td className="p-2 font-mono text-xs text-muted-foreground">{c.key}</td>
                  <td className="p-2"><Input value={c.label} onChange={(e) => setCfg({ ...cfg, cogs_composition: cfg.cogs_composition.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })} className="rounded-sm h-8 text-sm" /></td>
                  <td className="p-2 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Input type="number" step="0.1"
                               value={c.uplift_pct ?? 0}
                               disabled={!c.enabled || c.key === "goods"}
                               onChange={(e) => setCfg({ ...cfg, cogs_composition: cfg.cogs_composition.map((x, j) => j === i ? { ...x, uplift_pct: Number(e.target.value) } : x) })}
                               className="rounded-sm h-8 w-16 text-right tabular-nums"
                               data-testid={`wc-cogs-uplift-${c.key}`}
                               placeholder="0" />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                  </td>
                  <td className="p-2 text-center"><Switch checked={c.enabled} onCheckedChange={() => toggleCogs(i)} /></td>
                  <td className="p-2 text-right"><Button size="sm" variant="ghost" onClick={() => rmCogs(i)} className="h-7"><Trash2 className="w-3.5 h-3.5" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 text-[10px] text-muted-foreground">
            Uplift % on freight / packaging / installation lines is added on top of the goods purchase cost when posting DR-COGS on each sales invoice. The <span className="font-mono">goods</span> line is always 100% and cannot be uplifted.
          </div>
        </Card>

        <Card className="rounded-sm p-4 space-y-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">// Misc org-wide switches</div>
          </div>
          <label className="flex items-center justify-between text-sm">
            <span>Master price list enabled</span>
            <Switch checked={cfg.master_price_list_enabled} onCheckedChange={(v) => setCfg({ ...cfg, master_price_list_enabled: v })} data-testid="wc-master-price" />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Batching required on inward (RM)</span>
            <Switch checked={cfg.batching_inward} onCheckedChange={(v) => setCfg({ ...cfg, batching_inward: v })} data-testid="wc-batching-in" />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Batching required on outward (FG)</span>
            <Switch checked={cfg.batching_outward} onCheckedChange={(v) => setCfg({ ...cfg, batching_outward: v })} data-testid="wc-batching-out" />
          </label>
          <div className="pt-3 border-t">
            <Label className="text-[10px] font-mono uppercase text-muted-foreground">Inventory valuation method</Label>
            <div className="mt-2 flex gap-2">
              {[
                { key: "weighted_average", label: "Weighted Average" },
                { key: "fifo",             label: "FIFO" },
              ].map((m) => (
                <button key={m.key}
                          type="button"
                          onClick={() => setCfg({ ...cfg, valuation_method: m.key })}
                          className={`flex-1 rounded-sm border px-3 py-2 text-sm text-left ${cfg.valuation_method === m.key ? "border-accent bg-accent/10 text-accent" : "border-muted-foreground/20 hover:border-accent/50"}`}
                          data-testid={`wc-valuation-${m.key}`}>
                  <div className="text-[10px] font-mono uppercase">{cfg.valuation_method === m.key ? "// selected" : "// available"}</div>
                  <div className="font-medium">{m.label}</div>
                </button>
              ))}
            </div>
            <div className="text-[10px] text-muted-foreground mt-2">
              Applied to `/stock/summary` valuations · Reports · COGS. Weighted-Average uses cumulative signed value; FIFO consumes lots oldest-first.
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
